// @flow
import { isPlainObject } from "../../utils/helpers/objects";
import formatValue from "../../utils/number/formatValue";
import {
  parseNumberToString,
  parseToNumber,
} from "../../utils/number/parseNumbers";

import AttributeModel from "./AttributeModel";
import ConstraintCollection from "./input-constraints/ConstraintCollection";

import { ATTRIBUTE_WIDTH } from "../../constants";
import {
  CASEVIEW_LINK,
  INITIAL_TOTAL_FILESIZE,
  MAX_TOTAL_FILESIZE,
} from "../../constants/LayoutHints";

import NumberBoundaryConstraint from "./input-constraints/NumberBoundaryConstraint";
import NumberFormatConstraint from "./input-constraints/NumberFormatConstraint";
import NumberGroupingConstraint from "./input-constraints/NumberGroupingConstraint";

import type { AttributeType } from "../types";

/**
 * Number attribute
 */
export default class NumberAttributeModel extends AttributeModel {
  /**
   */
  static isApplicableModel(contributions: Object): boolean {
    return contributions.type === "number" || contributions.type === "integer";
  }

  /**
   */
  get type(): string {
    return "number";
  }

  /**
   * Get initial value
   */
  getInitialInputValue(value?: string): string {
    if (value == null) {
      return "";
    }

    return this.formatValue(value);
  }

  /**
   * Get grouping separator
   */
  get groupingSeparator(): string {
    return this.getContribution("groupingSeparator", "");
  }

  /**
   * Get decimal separator
   */
  get decimalSeparator(): string {
    return this.getContribution("decimalSeparator", "");
  }

  /**
   * Get minimum value
   */
  get minNumber(): ?number {
    return this.contributions.minimum;
  }

  /**
   * Get maximum value
   */
  get maxNumber(): ?number {
    return this.contributions.maximum;
  }

  /**
   * Gets the maximum digits based on the format
   */
  get maxDigits(): number {
    const format = this.format;

    if (format?.includes(".")) {
      // $FlowIssue
      return format.split(".")[1].length;
    }

    return 0;
  }

  /**
   * Get unit text
   */
  get unit(): string {
    return this.getContribution("unit", "");
  }

  /**
   * Get placeholder text
   */
  get placeholder(): string {
    return this.getContribution("placeholder", "");
  }

  /**
   * Add number constraints
   */
  addConstraints(): ConstraintCollection {
    const constraints = new ConstraintCollection();

    constraints.add(
      new NumberFormatConstraint(
        this.groupingSeparator,
        this.decimalSeparator,
        this.format,
      ),
    );
    constraints.add(
      new NumberGroupingConstraint(
        this.groupingSeparator,
        this.decimalSeparator,
        this.format,
      ),
    );
    constraints.add(
      new NumberBoundaryConstraint(
        this.minNumber,
        this.maxNumber,
        this.groupingSeparator,
        this.decimalSeparator,
        this.format,
      ),
    );

    return constraints;
  }

  /**
   * Reset attribute to empty string
   */
  reset() {
    this.inputvalue = "";
  }

  /**
   */
  formatValue(value: ?number | ?string): string {
    if (value == null || value.toString() === "") {
      return "";
    }

    return formatValue(
      value,
      this.format,
      this.groupingSeparator,
      this.decimalSeparator,
    );
  }

  /**
   */
  get readonlyvalue(): string {
    if (!isPlainObject(this.value)) {
      return this.formatValue(this.value);
    }

    return this.value;
  }

  /**
   * Returns the value as entered by the user. This can differ from the internal iso value that is stored
   */
  get inputvalue(): string {
    return this.getInputValue();
  }

  /**
   * Sets the input value to the value entered by the user
   */
  set inputvalue(value: string) {
    this._inputvalue = value;
    this.validate(value);

    const outputNumber = parseToNumber(
      value,
      this.groupingSeparator,
      this.decimalSeparator,
    );

    this.value = isNaN(outputNumber) ? null : parseNumberToString(outputNumber);
  }

  /**
   * Getting the value of the attribute
   */
  getValue(): number | null {
    const value = this._value;

    return value == null || value === "" ? null : Number(value);
  }

  /**
   * Update the attribute by name and value
   */
  update(value: string): NumberAttributeModel {
    if (value === this.inputvalue) {
      return this;
    }

    this.updateLastModification();
    this.inputvalue = value || "";

    return this;
  }

  /**
   */
  updateByAttribute(otherAttribute: AttributeType): void {
    this.reset();
    if (otherAttribute instanceof NumberAttributeModel) {
      this.update(otherAttribute.value);
    } else {
      console.error(
        `update of number attribute with ${otherAttribute.type} not supported`,
      );
    }
  }

  /**
   */
  get isHidden(): boolean {
    return (
      super.isHidden ||
      this.layouthint.has(
        INITIAL_TOTAL_FILESIZE,
        MAX_TOTAL_FILESIZE,
        CASEVIEW_LINK,
      )
    );
  }

  /**
   */
  set isHidden(isHidden: boolean) {
    super.isHidden = isHidden;
  }

  /**
   */
  get readonlyWidth(): $Keys<typeof ATTRIBUTE_WIDTH> {
    return ATTRIBUTE_WIDTH.SMALL;
  }
}
